/**
 * Orientation-details save flows: create, and details-modal update.
 *
 * Reducer calls in SDK v2 return a promise that resolves when the module
 * COMMITS the call and REJECTS with the server's refusal string (e.g. "Not an
 * admin of this site" from `require_site_admin` when the caller has no active
 * `user_site` row for the target project). The old save path fired the
 * reducers without awaiting them and toasted success immediately, so a
 * refused create looked exactly like a successful save until the next refresh
 * (issue #76, ludvig-testing-hermosa). These flows AWAIT every write, in
 * order, so the caller runs its success effects (picker selection, closing
 * the modal, the toast) only after the module has actually committed — and a
 * refusal rejects the whole flow for `withConn` to surface loudly.
 */

import type { OrientationVersion } from '@/generated/minetwin/types';
import type { StatusTag } from './model';
import type { OrientationDraft } from './OrientationDetails';

/** "mgr, operator , " -> ['mgr', 'operator'] (the occupation-target input). */
export const splitOcc = (s: string): string[] => s.split(',').map((x) => x.trim()).filter(Boolean);

/**
 * The subset of the minetwin module's reducers these flows call. Structural on
 * purpose: production passes `conn.reducers`, tests pass a recording fake.
 * Method syntax (not property arrows) so the generated reducer signatures —
 * whose optional columns type as optional keys — stay assignable.
 */
export type OrientationSaveReducers = {
  createOrientation(args: {
    orientationId: string;
    projectId: string;
    displayName: string;
    occupationTarget: string[];
    description: string;
    autoAssign: boolean;
    defaultVersion: OrientationVersion;
  }): Promise<void>;
  updateOrientation(args: {
    orientationId: string;
    displayName: string;
    occupationTarget: string[];
    description: string;
    autoAssign: boolean;
    defaultVersion: OrientationVersion;
  }): Promise<void>;
  setOrientationCheckSettings(args: {
    orientationId: string;
    passingScorePct: number | undefined;
    showAnswersAtEnd: boolean;
    autoReassignOnFail: boolean;
    maxAttempts: number | undefined;
  }): Promise<void>;
  setOrientationStatus(args: {
    orientationId: string;
    status: { tag: StatusTag };
  }): Promise<void>;
};

/**
 * The four check settings live on their own reducer (update_orientation does
 * not carry them), so every details save writes both. Max attempts only bites
 * while auto re-assign is on; sending it otherwise would resurrect a stale cap
 * the moment auto re-assign is turned back on.
 */
export function checkSettingsArgs(orientationId: string, d: OrientationDraft) {
  return {
    orientationId,
    passingScorePct: d.passingScorePct ?? undefined,
    showAnswersAtEnd: d.showAnswersAtEnd,
    autoReassignOnFail: d.autoReassignOnFail,
    maxAttempts: (d.autoReassignOnFail ? d.maxAttempts : null) ?? undefined,
  };
}

/**
 * Create a new (Draft) orientation, then write its check settings. Sequential
 * on purpose: a refused create (wrong site, no role) must reject BEFORE the
 * settings call fires at a row that does not exist, so the caller surfaces the
 * create's own refusal, not a confusing follow-on error.
 */
export async function runCreateOrientation(
  reducers: OrientationSaveReducers,
  orientationId: string,
  projectId: string,
  d: OrientationDraft,
  defaultVersion: OrientationVersion,
): Promise<void> {
  await reducers.createOrientation({
    orientationId,
    projectId,
    displayName: d.displayName,
    occupationTarget: splitOcc(d.occupationTarget),
    description: d.description,
    autoAssign: d.autoAssign,
    defaultVersion,
  });
  await reducers.setOrientationCheckSettings(checkSettingsArgs(orientationId, d));
}

/**
 * Save the details modal for an existing orientation: field patch + check
 * settings, and — only when the modal actually changed it — the lifecycle
 * status (its own reducer; update_orientation does not carry status).
 */
export async function runSaveDetails(
  reducers: OrientationSaveReducers,
  orientationId: string,
  d: OrientationDraft,
  previousStatus: StatusTag,
  defaultVersion: OrientationVersion,
): Promise<void> {
  await reducers.updateOrientation({
    orientationId,
    displayName: d.displayName,
    occupationTarget: splitOcc(d.occupationTarget),
    description: d.description,
    autoAssign: d.autoAssign,
    defaultVersion,
  });
  await reducers.setOrientationCheckSettings(checkSettingsArgs(orientationId, d));
  if (d.status !== previousStatus) {
    await reducers.setOrientationStatus({ orientationId, status: { tag: d.status } });
  }
}
